import arrayFrom from 'array-from';
import CustomEvent from 'custom-event';
import moment from 'moment-timezone';

// Adds a class to one element or a collection of elements.
function addClass(element, className) {
  if (!element) {
    return;
  }

  if (
    Object.prototype.toString.call(element) === '[object NodeList]' ||
    Object.prototype.toString.call(element) === '[object Array]' ||
    Object.prototype.toString.call(element) === '[object HTMLCollection]'
  ) {
    arrayFrom(element).forEach(subelement => addClass(subelement, className));
    return;
  }

  const classes = (element.getAttribute('class') || '').split(' ').filter(Boolean).filter(cn => cn !== className);
  classes.push(className);
  element.setAttribute('class', classes.join(' '));
}

// Removes a class from one element or a collection of elements.
function removeClass(element, className) {
  if (!element) {
    return;
  }

  if (
    Object.prototype.toString.call(element) === '[object NodeList]' ||
    Object.prototype.toString.call(element) === '[object Array]' ||
    Object.prototype.toString.call(element) === '[object HTMLCollection]'
  ) {
    arrayFrom(element).forEach(subelement => removeClass(subelement, className));
    return;
  }

  const classes = (element.getAttribute('class') || '').split(' ').filter(Boolean).filter(cn => cn !== className);
  element.setAttribute('class', classes.join(' '));
}

// Checks whether an element has a specific class.
function hasClass(element, className) {
  if (!element) {
    return false;
  }

  return (element.getAttribute('class') || '').split(' ').some(cn => cn === className);
}

// Toggles a class on an element.
function toggleClass(element, className) {
  if (!element) {
    return;
  }

  if (hasClass(element, className)) {
    removeClass(element, className);
  } else {
    addClass(element, className);
  }
}

// Returns the given node and all parent nodes up to the document.
function getParents(node) {
  const nodeParents = [];
  let iterateNode = node;
  while (iterateNode && iterateNode !== document) {
    nodeParents.push(iterateNode);
    iterateNode = iterateNode.parentNode || iterateNode.parentElement;
  }

  return nodeParents;
}

// Checks whether a value can be treated as a number.
function isNumber(number) {
  return !isNaN(number - 0) && number !== null && number !== '' && number !== false;
}

// Finds the closest parent element that contains the given class.
function findParent(element, selector) {
  let { parentElement } = element;

  if (parentElement !== null) {
    if (!parentElement.classList.contains(selector)) {
      parentElement = findParent(parentElement, selector);
    }
  }

  return parentElement;
}

// Returns the viewport size without scrollbars when possible.
function getWindowSize() {
  /* === grab the correct inner size without scrollbar - var innerWidth = zo.innerSize().width; === */
  let v = window;
  let a = 'inner';

  if (!('innerWidth' in v)) {
    a = 'client';
    v = document.documentElement || document.body;
  }

  return {
    width: v[`${a}Width`],
    height: v[`${a}Height`],
  };
}

// Dispatches a custom event on the window with an optional payload.
function dispatchCustomEvent(eventType, payload) {
  const customEvent = new CustomEvent(
    eventType,
    { detail: payload },
  );

  // dispatch event for project
  window.dispatchEvent(customEvent);
}

// Wraps an element in a newly created parent element.
function wrapElement(element, wrapNodeName, wrapClass = '') {
  const wrapperNode = document.createElement(wrapNodeName);
  if (wrapClass) {
    addClass(wrapperNode, wrapClass);
  }

  element.parentNode.insertBefore(wrapperNode, element);
  wrapperNode.appendChild(element);
  return wrapperNode;
}

// Generates SVG symbol markup for an icon name.
function generateSVGIcon(iconName, className = null, focusAble = null, style = null, ariaHidden = null) {
  let classContent = '';
  if (className) {
    classContent = ` class="${className}"`;
  }

  let focusContent = '';
  if (focusAble) {
    focusContent = `focusable=${focusAble}`;
  }

  let styleContent = '';
  if (style) {
    styleContent = `style=${style}`;
  }

  let ariaHiddenContent = ariaHidden;
  if (ariaHidden) {
    ariaHiddenContent = 'aria-hidden="true"';
  }

  return `<svg${classContent} ${focusContent} ${styleContent} ${ariaHiddenContent}><use xlink:href="#svgsymbol-${iconName}" /></svg>`;
}

/**
 * Replaces one placeholder token in a string.
 * @param content
 * @param placeholder
 * @param value
 * @returns {*}
 */
function replacePlaceholder(content, placeholder, value) {
  if (value || value === '') {
    return content.replace(`~${placeholder}~`, value);
  }

  return content;
}

// Returns the configured width for a named viewport.
function getViewportWidth(viewports, viewportKey) {
  const viewportForKey = viewports.filter(value => value.name === viewportKey);
  if (viewportForKey.length === 1) {
    return viewportForKey[0].width;
  }

  return 0;
}

// Converts a hex color into an rgba string.
function hexToRgb(hexNumber, alphaChannel) {
  let rgba = '';
  const alpha = alphaChannel;
  let hex = hexNumber;
  hex = hex.replace('#', '');
  const r = parseInt(hex.length === 3 ? hex.slice(0, 1).repeat(2) : hex.slice(0, 2), 16);
  const g = parseInt(hex.length === 3 ? hex.slice(1, 2).repeat(2) : hex.slice(2, 4), 16);
  const b = parseInt(hex.length === 3 ? hex.slice(2, 3).repeat(2) : hex.slice(4, 6), 16);
  if (alpha) {
    rgba = `rgba(${r},${g},${b},${alpha})`;
  } else {
    rgba = `rgba(${r},${g},${b})`;
  }

  return rgba;
}

// Loads JSON from a URL and stores it in sessionStorage.
async function loadJsonFileIntoSessionStorage(jsonFile, sessionStorageKey, disableCache) {
  // load json data from URL and add it to the session storage
  const cacheOption = {};

  if (disableCache) {
    cacheOption.cache = 'no-store';
  }

  await fetch(jsonFile, cacheOption)
    .then(response => response.json())
    .then((data) => {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(data));
    })
    .catch((error) => {
      console.error('Error fetching the JSON:', error);
    });
}

// Removes lazy loading from images and runs a callback after they are loaded.
function removeLazyLoadingAndExecute(callback) {
  const lazyImages = document.querySelectorAll('img[loading="lazy"]');
  const promises = [];

  lazyImages.forEach((img) => {
    img.removeAttribute('loading');

    // Create a Promise that waits for the image to fully load
    const imageLoadPromise = new Promise((resolve) => {
      if (img.complete) {
        resolve();
      } else {
        // eslint-disable-next-line no-param-reassign
        img.onload = () => resolve();
      }
    });

    promises.push(imageLoadPromise);
  });

  // Wait for all images to load before start callback
  Promise.all(promises).then(() => {
    if (typeof callback === 'function') {
      callback();
    }
  });
}

// Converts an ISO date to the local date format.
function formatDate(isoDate, langCode) {
  if (!isoDate) {
    return null;
  }

  const date = new Date(isoDate);

  return date.toLocaleDateString(langCode);
}

/**
 * Reads specific query parameters from the current URL.
 * @param {string} paramName - the name of the parameter
 * @returns {Array<string>} - all values for this parameter
 */
function getQueryParamsArray(paramName) {
  const params = new URLSearchParams(window.location.search);
  return params.getAll(paramName);
}

// Returns the localized CET/CEST label for a Berlin timestamp.
function getTimeZoneLabel(unixtimestamp, language) {
  const day = moment(unixtimestamp).tz('Europe/Berlin');
  const isSummerTime = day.isDST();

  if (language === 'en') {
    return isSummerTime ? 'CEST' : 'CET';
  }

  return isSummerTime ? 'MESZ' : 'MEZ';
}

// Enables pointer dragging for scrollable elements without blocking normal clicks.
function enableDragScroll(element, options = {}) {
  if (!element) {
    return () => {};
  }

  const dragElement = element;
  const axis = options.axis || 'x';
  const draggingClass = options.draggingClass || 'is-dragging';
  const dragThreshold = options.dragThreshold || 5;
  const preventClickAfterDrag = options.preventClickAfterDrag !== false;
  const pointerTypes = options.pointerTypes || null;
  const lockDirection = options.lockDirection !== false;
  let dragStartX = 0;
  let dragStartY = 0;
  let scrollStartLeft = 0;
  let scrollStartTop = 0;
  let isDragging = false;
  let hasDragged = false;

  // Checks whether the element can scroll on the configured axis.
  const isScrollable = () => {
    if (axis === 'y') {
      return dragElement.scrollHeight > dragElement.clientHeight;
    }

    return dragElement.scrollWidth > dragElement.clientWidth;
  };

  // Stops an active drag and releases pointer capture.
  const stopDragging = (event) => {
    if (!isDragging) {
      return;
    }

    isDragging = false;
    dragElement.classList.remove(draggingClass);

    if (dragElement.hasPointerCapture(event.pointerId)) {
      dragElement.releasePointerCapture(event.pointerId);
    }
  };

  // Starts drag tracking when the element is scrollable.
  const onPointerDown = (event) => {
    if (pointerTypes && pointerTypes.indexOf(event.pointerType) === -1) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    if (!isScrollable()) {
      return;
    }

    dragStartX = event.clientX;
    dragStartY = event.clientY;
    scrollStartLeft = dragElement.scrollLeft;
    scrollStartTop = dragElement.scrollTop;
    isDragging = true;
    hasDragged = false;
    dragElement.setPointerCapture(event.pointerId);
  };

  // Updates scroll position while dragging.
  const onPointerMove = (event) => {
    if (!isDragging) {
      return;
    }

    const distanceX = event.clientX - dragStartX;
    const distanceY = event.clientY - dragStartY;
    const dragDistance = axis === 'y' ? distanceY : distanceX;
    const crossDistance = axis === 'y' ? distanceX : distanceY;

    if (Math.abs(dragDistance) > dragThreshold) {
      if (lockDirection && Math.abs(crossDistance) > Math.abs(dragDistance)) {
        stopDragging(event);
        return;
      }

      hasDragged = true;
      dragElement.classList.add(draggingClass);
      event.preventDefault();
    }

    if (axis === 'y') {
      dragElement.scrollTop = scrollStartTop - distanceY;
    } else {
      dragElement.scrollLeft = scrollStartLeft - distanceX;
    }
  };

  // Prevents the synthetic click that can fire after a drag.
  const onClick = (event) => {
    if (preventClickAfterDrag && hasDragged) {
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    }
  };

  dragElement.addEventListener('pointerdown', onPointerDown);
  dragElement.addEventListener('pointermove', onPointerMove);
  dragElement.addEventListener('pointerup', stopDragging);
  dragElement.addEventListener('pointercancel', stopDragging);
  dragElement.addEventListener('click', onClick, true);

  return () => {
    dragElement.removeEventListener('pointerdown', onPointerDown);
    dragElement.removeEventListener('pointermove', onPointerMove);
    dragElement.removeEventListener('pointerup', stopDragging);
    dragElement.removeEventListener('pointercancel', stopDragging);
    dragElement.removeEventListener('click', onClick, true);
  };
}

export {
  addClass,
  hasClass,
  isNumber,
  findParent,
  getParents,
  removeClass,
  toggleClass,
  getWindowSize,
  dispatchCustomEvent,
  wrapElement,
  generateSVGIcon,
  replacePlaceholder,
  getViewportWidth,
  hexToRgb,
  loadJsonFileIntoSessionStorage,
  removeLazyLoadingAndExecute,
  formatDate,
  getQueryParamsArray,
  getTimeZoneLabel,
  enableDragScroll,
};
